import { CSSProperties, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Icons, IconType } from '../../../constants/icons';
import { SVGIconType } from '../../../constants/svgIcon';
import { ThemeType } from '../../../models/GameControllerStore';
import { GameType } from '../../../models/GameStore';
import { Profile } from '../../../models/UserStore';
import { getTimeString } from '../../../utils/date';
import { AvatarNode, SvgIcon } from '../../Molecules';
import { JoyajoyImage } from '../../Molecules';

import './GameStartInfoBoard.css';

export type GameStartInfoBoardProps = {
  clubIcon?: string;
  clubName?: string;
  profile?: Profile;
  //
  gameIcon?: IconType;
  gameTitle: string;
  challengeGameTotal?: number;
  //
  theme?: ThemeType;
  gameModeName?: string;
  typeName?: string;
  //
  latestScore?: number;
  latestTime?: number;
  //
  playCount?: number;
  avgScore?: number;
  //
  style?: CSSProperties;
  onClickShare?: () => void;
};

export const GameStartInfoBoard = memo(
  ({
    clubIcon,
    clubName,
    profile,
    gameIcon,
    gameTitle,
    challengeGameTotal,
    theme,
    gameModeName,
    typeName,
    latestScore,
    latestTime,
    playCount,
    avgScore,
    style,
    onClickShare,
  }: GameStartInfoBoardProps) => {
    const { t } = useTranslation();
    return (
      <div
        className={`info-board ${
          theme === GameType.FingerWriting ? 'orangeBar' : 'blueBar'
        }`}
        style={{ ...style }}
      >
        {onClickShare && (
          <div className="share-button" onClick={onClickShare}>
            <SvgIcon
              name={SVGIconType.Share}
              width={24}
              height={24}
              color="white"
            />
          </div>
        )}
        {clubName || profile ? (
          <div className="club-row">
            {clubIcon ? (
              <JoyajoyImage
                resource={{ uri: clubIcon }}
                className="club-icon"
              />
            ) : profile ? (
              <AvatarNode profile={profile} size={72} />
            ) : null}
            <p className="club-name">{clubName ?? profile?.name}</p>
          </div>
        ) : null}

        {clubName || profile ? <hr /> : null}

        <div className="game-row">
          {gameIcon && <img className="game-icon" src={Icons[gameIcon]} />}
          <div>
            <p className="game-title">{gameTitle}</p>
            {challengeGameTotal && (
              <p>{t('{count} Practice', { count: challengeGameTotal })}</p>
            )}
          </div>
        </div>

        {gameModeName || typeName ? (
          <div className="game-type-row">
            {gameModeName && <p className="text">{t(gameModeName)}</p>}
            {gameModeName && typeName && <p className="text">．</p>}
            {typeName && <p className="text">{t(typeName)}</p>}
          </div>
        ) : null}

        {latestScore || latestTime ? (
          <div className="latest-row">
            <p className="text">{t('Latest: ')}</p>
            {latestScore !== undefined ? (
              <>
                <SvgIcon name={SVGIconType.Score} className="small-icon" />

                <p className="text">{Math.floor(latestScore)}</p>
              </>
            ) : null}
            {latestTime !== undefined ? (
              <>
                <SvgIcon name={SVGIconType.Timer} className="small-icon" />
                <p className="text">{getTimeString(latestTime, 'M:ss.SS')}</p>
              </>
            ) : null}
          </div>
        ) : null}

        {playCount !== undefined || avgScore !== undefined ? (
          <div className="stats-row">
            <p className="small-text">
              {t('{count} Plays', { count: playCount ?? 0 })}
            </p>
            <p className="small-text">．{t('Average: ')}</p>
            <SvgIcon name={SVGIconType.Score} className="grey-small-icon" />

            <p className="small-text">{Math.floor(avgScore ?? 0)}</p>
          </div>
        ) : null}
      </div>
    );
  },
);
